/**
 * Asthra design-system components for the Word add-in.
 *
 * A typed React port of the design system's component bundle (V1.2), which
 * the add-in screen designs are drawn with. Same class names (``as-*``, styled
 * in ``asthra.css``), same markup, so a screen built from these renders like
 * its board. Differences from the bundle are only what a live pane needs:
 * controlled values, callbacks, and ``disabled``/``aria`` plumbing.
 *
 * SRP: presentation only. No fetches, no Word calls, no shell state.
 */

import type React from "react";
import { type ReactNode, useId } from "react";
import { MARK_PATH } from "./mark";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

// ---------------------------------------------------------------------------
// Icons: inline SVG, 16px box, stroke 1.6, currentColor
// ---------------------------------------------------------------------------

const ICON_PATHS = {
  check: ["M3.5 8.5l3 3 6-7"],
  flag: ["M4 14V2.5", "M4 3h7l-1.5 2.5L11 8H4"],
  chevron: ["M6 3.5l4.5 4.5L6 12.5"],
  search: ["M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10z", "M14 14l-3.5-3.5"],
  send: ["M8 13V3", "M3.5 7.5L8 3l4.5 4.5"],
  plus: ["M8 3v10", "M3 8h10"],
  back: ["M10 3.5L5.5 8l4.5 4.5"],
  up: ["M3.5 10L8 5.5l4.5 4.5"],
  down: ["M3.5 6L8 10.5 12.5 6"],
  close: ["M4 4l8 8", "M12 4l-8 8"],
  more: ["M3.5 8h.01", "M8 8h.01", "M12.5 8h.01"],
  download: ["M8 2.5v8", "M4.5 7L8 10.5 11.5 7", "M3 13.5h10"],
  external: ["M9 3h4v4", "M13 3L7.5 8.5", "M11 9.5V13H3V5h3.5"],
  file: ["M4 1.5h5.5L12.5 4.5v10H4z", "M9.5 1.5v3h3"],
  doc: ["M4 2.5h6l2.5 2.5v8.5H4z", "M6.5 8h4M6.5 10.5h3"],
  book: ["M2.5 3.5h4a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 0-1.5-1.5h-4z", "M13.5 3.5h-4A1.5 1.5 0 0 0 8 5v8a1.5 1.5 0 0 1 1.5-1.5h4z"],
  globe: [
    "M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13z",
    "M1.5 8h13",
    "M8 1.5c1.8 1.8 2.6 4 2.6 6.5S9.8 12.7 8 14.5C6.2 12.7 5.4 10.5 5.4 8S6.2 3.3 8 1.5z",
  ],
  quote: ["M3 9.5h3V13H3zM3 9.5C3 6.5 4 5 6 4.5", "M10 9.5h3V13h-3zM10 9.5c0-3 1-4.5 3-5"],
  info: ["M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13z", "M8 7.5V11", "M8 5h.01"],
} as const;

export type IconName = keyof typeof ICON_PATHS;

export const Icon: React.FC<{ name: IconName; className?: string }> = ({ name, className }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={name === "more" ? 2.4 : 1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={cx(name === "check" && "as-check", className)}
  >
    {ICON_PATHS[name].map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

export const Mark: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
  <svg className={className} style={style} viewBox="34 34 232 232" aria-label="Asthra" role="img">
    <path d={MARK_PATH} fill="var(--brand)" />
  </svg>
);

// ---------------------------------------------------------------------------
// Button, IconButton, Back link
// ---------------------------------------------------------------------------

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: "sm";
  block?: boolean;
  icon?: IconName;
  type?: "button" | "submit";
}

export const Button: React.FC<ButtonProps> = ({
  variant = "secondary",
  size,
  block,
  icon,
  className,
  children,
  type = "button",
  ...rest
}) => (
  <button
    // biome-ignore lint/a11y/useButtonType: type is always "button" or "submit"
    type={type}
    {...rest}
    className={cx("as-btn", `as-btn-${variant}`, size === "sm" && "as-btn-sm", block && "as-btn-block", className)}
  >
    {icon ? <Icon name={icon} /> : null}
    {/* Text in a span so a label too long for the button ends in an ellipsis
        rather than overflowing (a bare text node in a flex box cannot). */}
    {typeof children === "string" ? <span className="as-btn-label">{children}</span> : children}
  </button>
);

export const IconButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; outline?: boolean }
> = ({ icon, label, outline, className, ...rest }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    {...rest}
    className={cx("as-iconbtn", outline && "as-iconbtn-outline", className)}
  >
    <Icon name={icon} />
  </button>
);

/** "‹ Spine" / "‹ Back" link at the top of step and detail screens. */
export const BackLink: React.FC<{
  onClick: () => void;
  children: ReactNode;
  "aria-label"?: string;
  title?: string;
}> = ({ onClick, children, ...rest }) => (
  <button type="button" className="as-back as-back-btn" onClick={onClick} {...rest}>
    <Icon name="back" />
    {children}
  </button>
);

/** Text-only action in brand ink ("Change", "Select all", "See all drafts"). */
export const LinkButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  children,
  ...rest
}) => (
  <button type="button" {...rest} className={cx("as-linkbtn", className)}>
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// StatusPill, Tag
// ---------------------------------------------------------------------------

export type PillStatus = "live" | "drafting" | "review" | "done" | "queued" | "paused" | "failed";

const PILL_LABEL: Record<PillStatus, string> = {
  live: "Live",
  drafting: "Drafting",
  review: "Reviewing",
  done: "Done",
  queued: "Queued",
  paused: "Paused",
  failed: "Failed",
};

export const StatusPill: React.FC<{ status: PillStatus; children?: ReactNode }> = ({ status, children }) => (
  <span className={`as-pill as-pill-${status}`}>{children ?? PILL_LABEL[status]}</span>
);

export const Tag: React.FC<{ tone?: "brand"; children: ReactNode }> = ({ tone, children }) => (
  <span className={cx("as-tag", tone === "brand" && "as-tag-brand")}>{children}</span>
);

// ---------------------------------------------------------------------------
// ContextBar: mark, status, context chips, account avatar
// ---------------------------------------------------------------------------

export interface ContextChip {
  key: string;
  value: string;
  /** Hover title; the chip shows only ``value``. */
  title?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface ContextBarProps {
  status?: PillStatus | null;
  /** Status pill label override (e.g. "Sandbox"). */
  statusLabel?: string;
  onStatusClick?: () => void;
  chips: readonly ContextChip[];
  initials: string;
  onAccount?: () => void;
  accountExpanded?: boolean;
  /** White-label brands supply their own mark; Asthra uses the burst. */
  mark?: ReactNode;
}

export const ContextBar: React.FC<ContextBarProps> = ({
  status,
  statusLabel,
  onStatusClick,
  chips,
  initials,
  onAccount,
  accountExpanded,
  mark,
}) => (
  <header className="as-ctx">
    {mark ?? <Mark className="as-ctx-mark" />}
    {status ? (
      onStatusClick ? (
        <button type="button" className="as-pill-btn" onClick={onStatusClick} title="Switch mode">
          <StatusPill status={status}>{statusLabel}</StatusPill>
        </button>
      ) : (
        <StatusPill status={status}>{statusLabel}</StatusPill>
      )
    ) : null}
    <div className="as-ctx-chips">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          className={cx("as-chip", c.value.length <= 8 && "as-chip-fixed")}
          title={c.title ?? c.value}
          onClick={c.onClick}
          disabled={c.disabled}
        >
          <span>{c.value}</span>
        </button>
      ))}
    </div>
    <button
      type="button"
      className="as-avatar"
      aria-label="Account"
      aria-haspopup="menu"
      aria-expanded={accountExpanded}
      onClick={onAccount}
    >
      {initials}
    </button>
  </header>
);

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export interface TabItem<T extends string> {
  id: T;
  label: string;
  count?: string | number | null;
  alert?: boolean;
  live?: boolean;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
}: {
  items: ReadonlyArray<TabItem<T>>;
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="as-tabs" role="tablist">
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          className="as-tab"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
        >
          {t.live ? <span className="as-dot-live" aria-label="running" /> : null}
          {t.label}
          {t.count != null && t.count !== "" ? (
            <span className={cx("as-count", t.alert && "as-count-alert")}>{t.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** Segmented control ("Open 4 · Resolved 1", "New text · Show changes"). */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
  label: string;
}) {
  return (
    <div className="as-seg" role="tablist" aria-label={label}>
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          className="as-seg-btn"
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProgressBar
// ---------------------------------------------------------------------------

export const ProgressBar: React.FC<{
  value: number;
  total: number;
  /** ``false`` hides the meta row. */
  label?: ReactNode | false;
}> = ({ value, total, label }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className={cx("as-progress", pct === 100 && "as-progress-done")}>
      {label !== false ? (
        <div className="as-progress-meta">
          <span>{label ?? `${value} of ${total} sections`}</span>
          <span>{pct}%</span>
        </div>
      ) : null}
      <div
        className="as-progress-track"
        role="progressbar"
        aria-label={typeof label === "string" ? label : "Progress"}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="as-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SectionRow
// ---------------------------------------------------------------------------

export interface SectionRowProps {
  number?: string;
  title: string;
  status: PillStatus;
  statusLabel?: string;
  step?: ReactNode;
  flags?: number;
  web?: number;
  elapsed?: string;
  current?: boolean;
  /** Dim the row (e.g. paused while a stop is being confirmed). */
  muted?: boolean;
  onClick?: () => void;
}

export const SectionRow: React.FC<SectionRowProps> = ({
  number,
  title,
  status,
  statusLabel,
  step,
  flags,
  web,
  elapsed,
  current,
  muted,
  onClick,
}) => {
  const busy = status === "drafting" || status === "review";
  return (
    <button
      type="button"
      className={cx("as-srow", muted && "as-srow-muted")}
      aria-current={current ? "true" : undefined}
      onClick={onClick}
      title="Go to this section in the document"
    >
      <span className="as-srow-num">{number ?? ""}</span>
      <span style={{ minWidth: 0 }}>
        <span className="as-srow-title">{title}</span>
        <span className="as-srow-sub">
          <StatusPill status={status}>{statusLabel}</StatusPill>
          {step ? <span className="as-srow-step">{step}</span> : null}
        </span>
      </span>
      <span className="as-srow-end">
        {flags ? (
          <span className="as-flag" title={`${flags} open issues`}>
            <Icon name="flag" />
            {flags}
          </span>
        ) : null}
        {web ? (
          <span className="as-web" title={`Searched the web ${web} ${web === 1 ? "time" : "times"}; see Refs`}>
            <Icon name="globe" />×{web}
          </span>
        ) : null}
        {elapsed ? <span>{elapsed}</span> : null}
        {busy ? <span className="as-spin" aria-label="working" /> : status === "done" ? <Icon name="check" /> : null}
      </span>
    </button>
  );
};

// ---------------------------------------------------------------------------
// IssueCard
// ---------------------------------------------------------------------------

export type IssueKind = "missing" | "language" | "consistency";

const ISSUE_KIND_LABEL: Record<IssueKind, string> = {
  missing: "Missing info",
  language: "Language",
  consistency: "Consistency",
};

export const IssueCard: React.FC<{
  kind: IssueKind;
  /** Kind label override (e.g. "Regulatory"). */
  kindLabel?: string;
  section: string;
  selected?: boolean;
  resolved?: boolean;
  onSelect?: (selected: boolean) => void;
  /** Shown under the text when the card is selected. */
  actions?: ReactNode;
  children: ReactNode;
}> = ({ kind, kindLabel, section, selected, resolved, onSelect, actions, children }) => (
  <div className="as-issue" data-selected={selected ? "true" : undefined} data-resolved={resolved ? "true" : undefined}>
    <input
      type="checkbox"
      className="as-cb"
      checked={!!selected}
      onChange={(e) => onSelect?.(e.target.checked)}
      aria-label="Select issue"
      disabled={resolved}
    />
    <div style={{ minWidth: 0 }}>
      <div className="as-issue-head">
        <span className={`as-issue-kind as-issue-kind-${kind}`}>{kindLabel ?? ISSUE_KIND_LABEL[kind]}</span>
        <span className="as-issue-ref">{resolved ? `Resolved · ${section}` : section}</span>
      </div>
      <div className="as-issue-text">{children}</div>
    </div>
    {selected && !resolved && actions ? <div className="as-issue-actions">{actions}</div> : null}
  </div>
);

// ---------------------------------------------------------------------------
// Citation, Gap, Notice
// ---------------------------------------------------------------------------

export const Citation: React.FC<{ onClick?: () => void; children: ReactNode }> = ({ onClick, children }) => (
  <button type="button" className="as-cite" title="Show the source passage" onClick={onClick}>
    {children}
  </button>
);

export const Gap: React.FC<{ children?: ReactNode }> = ({ children }) => (
  <mark className="as-gap" title="Not found in your source library">
    {children ?? "[not in sources]"}
  </mark>
);

export type NoticeTone = "info" | "warning" | "danger" | "success";

export const Notice: React.FC<{
  tone?: NoticeTone;
  title?: ReactNode;
  action?: ReactNode;
  onAction?: () => void;
  children?: ReactNode;
}> = ({ tone = "info", title, action, onAction, children }) => (
  <div className={`as-notice as-notice-${tone}`} role={tone === "danger" ? "alert" : "status"}>
    {title ? <b>{title}</b> : null}
    {children ? <div>{children}</div> : null}
    {action ? (
      <button className="as-notice-action" type="button" onClick={onAction}>
        {action}
      </button>
    ) : null}
  </div>
);

// ---------------------------------------------------------------------------
// ChoiceList (radio list)
// ---------------------------------------------------------------------------

export interface Choice<T extends string> {
  id: T;
  label: ReactNode;
  meta?: ReactNode;
  disabled?: boolean;
  /** Trailing chevron that opens the item (e.g. a library's files). */
  onMore?: () => void;
  moreLabel?: string;
  /** Icon for the trailing button; a chevron unless said. */
  moreIcon?: IconName;
  /** For a trailing button that shows and hides details: whether they show. */
  moreExpanded?: boolean;
}

export function ChoiceList<T extends string>({
  label,
  options,
  value,
  onChange,
  name,
}: {
  label: string;
  options: ReadonlyArray<Choice<T>>;
  value: T | null;
  onChange: (id: T) => void;
  name?: string;
}) {
  const autoName = useId();
  return (
    <div className="as-choices" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <label key={o.id} className="as-choice">
          <input
            type="radio"
            value={o.id}
            name={name ?? autoName}
            checked={value === o.id}
            disabled={o.disabled}
            onChange={() => onChange(o.id)}
          />
          <span style={{ minWidth: 0 }}>
            <span className="as-choice-name">{o.label}</span>
            {o.meta ? <div className="as-choice-meta">{o.meta}</div> : null}
          </span>
          {o.onMore ? (
            <IconButton
              icon={o.moreIcon ?? "chevron"}
              label={o.moreLabel ?? "Open"}
              aria-expanded={o.moreExpanded}
              className="as-choice-end"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                o.onMore?.();
              }}
            />
          ) : (
            <span />
          )}
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TextField, SearchField
// ---------------------------------------------------------------------------

export interface TextFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  multiline?: boolean;
  icon?: IconName;
  help?: ReactNode;
  error?: ReactNode;
  autoComplete?: string;
  disabled?: boolean;
  id?: string;
  maxLength?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  inputRef?: React.Ref<HTMLInputElement>;
}

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  multiline,
  icon,
  help,
  error,
  autoComplete,
  disabled,
  id,
  maxLength,
  onKeyDown,
  inputRef,
}) => {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const input = multiline ? (
    <textarea
      id={fieldId}
      className="as-input"
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      aria-label={label ? undefined : placeholder}
    />
  ) : (
    <input
      id={fieldId}
      ref={inputRef}
      className="as-input"
      type={type}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      aria-label={label ? undefined : placeholder}
      aria-invalid={error ? "true" : undefined}
    />
  );
  return (
    <div className="as-field">
      {label ? (
        <label className="as-field-label" htmlFor={fieldId}>
          {label}
        </label>
      ) : null}
      {icon ? (
        <div className="as-input-wrap">
          <Icon name={icon} />
          {input}
        </div>
      ) : (
        input
      )}
      {error ? <div className="as-field-error">{error}</div> : help ? <div className="as-field-help">{help}</div> : null}
    </div>
  );
};

export const SearchField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <TextField icon="search" type="search" value={value} onChange={onChange} placeholder={placeholder} />
);

/** Native select styled as an input (Workspace, filters). */
export const Select: React.FC<
  React.SelectHTMLAttributes<HTMLSelectElement> & { label?: string; inline?: boolean }
> = ({ label, inline, className, children, ...rest }) => {
  const autoId = useId();
  const id = rest.id ?? autoId;
  return (
    <div className={cx(inline ? "as-field-inline" : "as-field")}>
      {label ? (
        <label className={inline ? "as-field-inline-label" : "as-field-label"} htmlFor={id}>
          {label}
        </label>
      ) : null}
      <select {...rest} id={id} className={cx("as-input as-select", className)}>
        {children}
      </select>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stepper, DocCard
// ---------------------------------------------------------------------------

export const Stepper: React.FC<{ steps: readonly string[]; current: number }> = ({ steps, current }) => (
  <ol className="as-steps">
    {steps.map((s, i) => {
      const state = i < current ? "done" : i === current ? "current" : "todo";
      return [
        i ? <li key={`sep-${s}`} className="as-step-sep" aria-hidden="true" /> : null,
        <li key={s} className="as-step" data-state={state} aria-current={state === "current" ? "step" : undefined}>
          <span className="as-step-n">{state === "done" ? "✓" : i + 1}</span>
          {s}
        </li>,
      ];
    })}
  </ol>
);

export const DocCard: React.FC<{
  type: string;
  status: PillStatus;
  statusLabel?: string;
  name: string;
  meta: string;
  done?: number;
  total?: number;
  onClick?: () => void;
  /** Overflow menu or other trailing control, outside the card button. */
  menu?: ReactNode;
}> = ({ type, status, statusLabel, name, meta, done, total, onClick, menu }) => (
  <div className="as-doccard-wrap">
    <button type="button" className="as-doccard" onClick={onClick}>
      <span className="as-doccard-top">
        <Tag tone="brand">{type}</Tag>
        <StatusPill status={status}>{statusLabel}</StatusPill>
      </span>
      <span style={{ minWidth: 0 }}>
        <span className="as-doccard-name" style={{ display: "block" }}>
          {name}
        </span>
        <span className="as-doccard-meta">{meta}</span>
      </span>
      {total ? <ProgressBar value={done ?? 0} total={total} /> : null}
    </button>
    {menu}
  </div>
);

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

export const ComposerHint: React.FC = () => (
  <div className="as-composer-hint" style={{ marginTop: 6 }}>
    <span>
      <kbd>/</kbd> commands
    </span>
    <span>
      <kbd>Shift+Enter</kbd> new line
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Dialog, Sheet, Scrim
// ---------------------------------------------------------------------------

export const Dialog: React.FC<{
  title: ReactNode;
  description?: ReactNode;
  cancelLabel?: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  confirmVariant?: ButtonVariant;
  onCancel: () => void;
  onConfirm: () => void;
  children?: ReactNode;
}> = ({
  title,
  description,
  cancelLabel = "Cancel",
  confirmLabel,
  confirmDisabled,
  confirmVariant = "primary",
  onCancel,
  onConfirm,
  children,
}) => {
  const titleId = useId();
  return (
    <section className="as-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <h2 className="as-dialog-title" id={titleId}>
        {title}
      </h2>
      {description ? <p className="as-dialog-desc">{description}</p> : null}
      {children}
      <div className="as-dialog-actions">
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant={confirmVariant} disabled={confirmDisabled} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </section>
  );
};

/** Grey scrim over the pane with content (dialog or sheet) on top. */
export const Overlay: React.FC<{
  placement: "top" | "bottom";
  onDismiss?: () => void;
  children: ReactNode;
}> = ({ placement, onDismiss, children }) => (
  <div className={cx("as-overlay", `as-overlay-${placement}`)}>
    <button type="button" className="as-overlay-scrim as-scrim" aria-label="Close" tabIndex={-1} onClick={onDismiss} />
    <div className="as-overlay-body">{children}</div>
  </div>
);

export const Sheet: React.FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <section className="as-sheet" role="dialog" aria-label={label}>
    <span className="as-peek-grip" aria-hidden="true" />
    {children}
  </section>
);

// ---------------------------------------------------------------------------
// Small layout pieces the boards repeat
// ---------------------------------------------------------------------------

/** "FOLDERS", "RECENT", "CLINICAL" — small uppercase section label. */
export const Eyebrow: React.FC<{ children: ReactNode; as?: "h2" | "h3" | "div"; mono?: boolean }> = ({
  children,
  as: Tag2 = "h2",
  mono,
}) => <Tag2 className={cx("as-eyebrow", mono && "as-eyebrow-mono")}>{children}</Tag2>;

/** Two-button footer bar: secondary (1fr) + primary (2fr), as on every step. */
export const FooterBar: React.FC<{ note?: ReactNode; children: ReactNode }> = ({ note, children }) => (
  <div className="as-footer">
    {note ? <p className="as-footer-note">{note}</p> : null}
    <div className="as-footer-actions">{children}</div>
  </div>
);

/** Step header: back link + "New document · step 2 of 4", then the question. */
export const StepHeader: React.FC<{
  onBack?: () => void;
  backLabel?: string;
  onClose?: () => void;
  step: string;
  title: ReactNode;
  children?: ReactNode;
}> = ({ onBack, backLabel = "Back", onClose, step, title, children }) => (
  <div className="as-stephead">
    <div className="as-stephead-top">
      {onBack ? <BackLink onClick={onBack}>{backLabel}</BackLink> : <span className="as-stephead-step">{step}</span>}
      {onBack ? <span className="as-stephead-step">{step}</span> : null}
      {onClose ? <IconButton icon="close" label="Close" onClick={onClose} /> : null}
    </div>
    <h1 className="as-stephead-title">{title}</h1>
    {children}
  </div>
);
